// src/App.tsx
/**
 * Premium 3D Portfolio App
 *
 * Cinematic visual quality with smooth, reactive navigation.
 * Uses centralized scene system for consistency.
 */

import { Suspense, useState, useEffect, useCallback, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Preload } from '@react-three/drei'
import { Leva } from 'leva'

// Scene system
import {
  StageEnvironment,
  CameraSystem,
  palette,
  spacing,
  radii,
  world,
  OfficeRoom,
} from './scene'
import { StudioLighting } from './scene/LightingRig'
import { useDesktopLayout } from './scene/useResponsiveLayout'
import { useStageStore } from './scene/useStageStore'

// Stage components
import { IntroStage, ExperienceStage, ProjectsStage } from './scene/stages'

// Ambient Particles (drei)
import AmbientParticles from './scene/AmbientParticles'

// Error Boundary
import { ErrorBoundary } from './components/ErrorBoundary'

// Avatar & Voice
import Avatar3D from './components/Avatar3D'
import { VoiceProvider, useVoiceState } from './hooks/useVoice'
import { useTranscriptStore } from './hooks/useTranscriptStore'

// Configure unified Omote logger (from /logging subpath to avoid pulling ONNX Runtime)
import { configureLogging } from '@omote/core/logging'
import { configureModelUrls } from '@omote/core'

configureLogging({
  level: 'info',
  format: 'pretty',
})

// Point models at self-hosted CDN for fast, reliable delivery.
// If this call is removed, all models fall back to HuggingFace CDN automatically.
configureModelUrls({
  lam: '/models/lam_ios.onnx',
  senseVoice: '/models/sensevoice/model.int8.onnx',
  sileroVad: '/models/silero-vad.onnx',
})

// ============================================================================
// MAIN SCENE
// ============================================================================

function Scene() {
  return (
    <>
      {/* ================================================================ */}
      {/* CAMERA SYSTEM (spring-damped transitions + input handling)       */}
      {/* ================================================================ */}
      <CameraSystem />

      {/* ================================================================ */}
      {/* STUDIO LIGHTING                                                   */}
      {/* Wrapped in Suspense: loads HDRI environment map                  */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <StudioLighting intensity={1} />
      </Suspense>

      {/* ================================================================ */}
      {/* STAGE ENVIRONMENT (ground + shadows + fog)                       */}
      {/* ================================================================ */}
      <StageEnvironment
        groundY={world.ground.y}
        shadowType="contact"
        showFog={true}
      />

      {/* ================================================================ */}
      {/* OFFICE ROOM (Low Poly Room)                                     */}
      {/* Wrapped in Suspense: loads GLTF model                           */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <OfficeRoom
          position={[0, world.ground.y, -0.2]}
          rotation={[0, -.8, 0]}
          scale={0.7}
        />
      </Suspense>

      {/* ================================================================ */}
      {/* AMBIENT PARTICLES (sparkles + stars)                             */}
      {/* ================================================================ */}
      <AmbientParticles sparkles={true} stars={true} />

      {/* ================================================================ */}
      {/* AVATAR                                                           */}
      {/* Wrapped in Suspense: loads GLB + FBX                             */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <Avatar3D scale={0.95} />
      </Suspense>

      {/* ================================================================ */}
      {/* STAGE PANELS (positions from useStageStore)                      */}
      {/* ================================================================ */}
      <IntroStage />
      <ExperienceStage />
      <ProjectsStage />

    </>
  )
}

// ============================================================================
// TRANSCRIPT OVERLAY (HTML — outside R3F Canvas to avoid GPU contention)
// ============================================================================

function TranscriptOverlay() {
  const transcript = useTranscriptStore((s) => s.transcript)
  const interimTranscript = useTranscriptStore((s) => s.interimTranscript)
  const transcriptStatus = useTranscriptStore((s) => s.transcriptStatus)

  // Auto-hide: fade out 5s after last content change
  const [visible, setVisible] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const hasContent = !!(transcript.user || transcript.assistant || (transcriptStatus === 'interim' && interimTranscript))

  useEffect(() => {
    if (hasContent) {
      setVisible(true)
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = setTimeout(() => setVisible(false), 12000)
    }
    return () => clearTimeout(hideTimerRef.current)
  }, [transcript.user, transcript.assistant, interimTranscript, hasContent])

  // Show overlay if there's any text to display (including interim)
  const hasInterim = transcriptStatus === 'interim' && interimTranscript
  if (!hasContent && !visible) {
    return null
  }

  // Determine user text to display: interim (live) vs final (sent)
  const showInterim = hasInterim && !transcript.user
  const userDisplayText = showInterim
    ? interimTranscript + '...'
    : transcript.user

  // Visual distinction: interim = dimmer, final = brief green border accent
  const userTextColor = showInterim
    ? palette.text.secondary
    : palette.text.primary
  const borderColor = transcriptStatus === 'final'
    ? palette.success[500]
    : palette.border.accent

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: window.innerWidth <= 768 ? 12 : 40,
        paddingLeft: 16,
        paddingRight: 16,
        pointerEvents: 'none',
        zIndex: 20,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.6s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.sm,
          padding: spacing.md,
          background: palette.glass.background,
          borderRadius: radii.lg,
          width: '100%',
          maxWidth: 500,
          border: `1px solid ${borderColor}`,
          backdropFilter: 'blur(12px)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {userDisplayText && (
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
            <span
              style={{
                fontSize: 12,
                color: palette.success[500],
                fontWeight: 'bold',
                flexShrink: 0,
              }}
            >
              You:
            </span>
            <span style={{ fontSize: 12, color: userTextColor, flexGrow: 1 }}>
              {userDisplayText}
            </span>
          </div>
        )}
        {transcript.assistant && (
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
            <span
              style={{
                fontSize: 12,
                color: palette.primary[500],
                fontWeight: 'bold',
                flexShrink: 0,
              }}
            >
              Sepehr:
            </span>
            <span style={{ fontSize: 12, color: palette.text.primary, flexGrow: 1 }}>
              {transcript.assistant}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// NAVIGATION HINT
// ============================================================================

function NavigationHint() {
  const { isDesktopLayout } = useDesktopLayout()
  const stageName = useStageStore((s) => s.currentStage.name)

  if (isDesktopLayout) return null

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 32,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      <div
        style={{
          padding: '6px 12px',
          background: 'rgba(9, 9, 11, 0.7)',
          borderRadius: 10,
          border: `1px solid ${palette.border.subtle}`,
          backdropFilter: 'blur(8px)',
        }}
      >
        <span style={{ fontSize: 12, color: palette.text.tertiary, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          Scroll or Swipe &bull; {stageName}
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// LOADING SCREEN (shown while R3F assets load)
// ============================================================================

function LoadingScreen({ ready }: { ready: boolean }) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (ready) {
      const timer = setTimeout(() => setVisible(false), 600)
      return () => clearTimeout(timer)
    }
  }, [ready])

  if (!visible) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: palette.background.deep,
        transition: 'opacity 0.5s ease-out',
        opacity: ready ? 0 : 1,
        pointerEvents: ready ? 'none' : 'auto',
      }}
    >
      <h1
        style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: '#ffffff',
          marginBottom: '1.5rem',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          letterSpacing: '-0.02em',
        }}
      >
        Sepehr Nourbakhsh
      </h1>
      <div
        style={{
          width: '200px',
          height: '2px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '1px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '40%',
            height: '100%',
            background: palette.primary[500],
            borderRadius: '1px',
            animation: 'loading-slide 1.2s ease-in-out infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes loading-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  )
}

// ============================================================================
// ASSET READY TRACKER (signals when R3F assets finish loading)
// ============================================================================

function AssetReadySignal({ onReady }: { onReady: () => void }) {
  const frameCount = useRef(0)

  // Wait for the renderer to warm up (shader compilation, texture uploads)
  // before signaling ready. This prevents the loading screen from fading
  // out while the GPU is still compiling shaders on first render.
  useFrame(() => {
    frameCount.current++
    if (frameCount.current === 30) {
      onReady()
    }
  })

  return null
}

// ============================================================================
// SCENE FAILED FALLBACK (shown when WebGL/Canvas crashes)
// ============================================================================

function SceneFailedFallback() {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: palette.background.deep,
        color: '#e0e0e0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', color: '#ffffff' }}>
        Sepehr Nourbakhsh
      </h1>
      <p style={{ fontSize: '1rem', color: '#999', marginBottom: '2rem' }}>
        Software Engineer &amp; AI Enthusiast
      </p>
      <p style={{ fontSize: '0.875rem', color: '#777', marginBottom: '2rem', maxWidth: '400px' }}>
        3D scene could not load. Here is my portfolio:
      </p>
      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <a
          href="https://github.com/sepehrnour"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#8ab4f8', textDecoration: 'none', fontSize: '0.95rem' }}
        >
          GitHub
        </a>
        <a
          href="https://linkedin.com/in/sepehr-nour"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#8ab4f8', textDecoration: 'none', fontSize: '0.95rem' }}
        >
          LinkedIn
        </a>
        <a
          href="mailto:sepehrnour@gmail.com"
          style={{ color: '#8ab4f8', textDecoration: 'none', fontSize: '0.95rem' }}
        >
          Email
        </a>
      </div>
    </div>
  )
}

// ============================================================================
// APP CONTENT (inside VoiceProvider to access loading state)
// ============================================================================

function AppContent() {
  const [r3fReady, setR3fReady] = useState(false)
  const [onnxStarted, setOnnxStarted] = useState(false)
  const { loadModels } = useVoiceState()
  const handleR3fReady = useCallback(() => setR3fReady(true), [])

  // Delay ONNX model loading until after R3F sweep-in animation completes.
  // Uses requestIdleCallback so loading starts when the browser is idle,
  // with a 5s minimum delay to ensure the camera sweep-in finishes first.
  useEffect(() => {
    if (r3fReady && !onnxStarted) {
      const timer = setTimeout(() => {
        const startLoad = () => {
          setOnnxStarted(true)
          loadModels()
        }
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(startLoad, { timeout: 2000 })
        } else {
          startLoad()
        }
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [r3fReady, onnxStarted, loadModels])

  return (
    <>
      {/* Loading screen — fades out when R3F assets finish rendering */}
      <LoadingScreen ready={r3fReady} />
      <Leva hidden />

      <TranscriptOverlay />
      <NavigationHint />

      <div style={{ width: '100%', height: '100%', background: palette.background.deep }}>
        <ErrorBoundary fallback={<SceneFailedFallback />}>
          <Canvas
            camera={{
              position: [0, 1.5, 4.5],
              fov: world.camera.fov,
              near: world.camera.near,
              far: world.camera.far,
            }}
            gl={{
              antialias: true,
              alpha: false,
              powerPreference: 'high-performance',
              // Reduce strain on mobile GPU
              pixelRatio: Math.min(window.devicePixelRatio, 2),
            }}
            shadows
            frameloop="always"
            onCreated={({ gl }) => {
              gl.setClearColor(palette.background.deep)
            }}
          >
            {/* Set scene background immediately to prevent black flash */}
            <color attach="background" args={[palette.background.deep]} />
            <Suspense fallback={null}>
              <Scene />
              <Preload all />
              {/* Fires after all Suspense children resolve (R3F assets loaded) */}
              <AssetReadySignal onReady={handleR3fReady} />
            </Suspense>
          </Canvas>
        </ErrorBoundary>
      </div>
    </>
  )
}

// ============================================================================
// APP ROOT
// ============================================================================

export default function App() {
  return (
    <VoiceProvider>
      <AppContent />
    </VoiceProvider>
  )
}
